import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Sprout, Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export function SeedCard({ onFinished }) {
  const [cfg, setCfg] = useState(null);
  const [region, setRegion] = useState("");
  const [max, setMax] = useState("60");
  const [run, setRun] = useState(null);
  useEffect(() => {
    api.get("/admin/seed/config").then((r) => setCfg(r.data)).catch(() => {});
    api.get("/admin/seed/runs").then((r) => { if (r.data[0]?.status === "running") setRun(r.data[0]); }).catch(() => {});
  }, []);
  useEffect(() => {
    if (!run || run.status !== "running") return undefined;
    const t = setInterval(async () => {
      try {
        const { data } = await api.get(`/admin/seed/${run.id}`);
        setRun(data);
        if (data.status !== "running") { onFinished(); toast.success(`Seed finished: ${data.added} added, ${data.skipped} skipped`); }
      } catch { setRun((p) => ({ ...p, status: "interrupted" })); }
    }, 2500);
    return () => clearInterval(t);
  }, [run, onFinished]);
  const start = async () => {
    try { setRun((await api.post("/admin/seed", { region, max_results: parseInt(max, 10) })).data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const running = run?.status === "running";
  return (
    <Card className="p-4 space-y-3" data-testid="seed-card">
      <div>
        <h3 className="font-display font-bold flex items-center gap-2"><Sprout className="h-4 w-4 text-kinetic" /> Find removalists with Google</h3>
        <p className="text-xs text-muted-foreground">Searches Google for "removalists" and "furniture movers" in a postcode or region, looks up each ABN, and checks their website for an email. New listings start not approved for leads.</p>
      </div>
      {cfg && !cfg.places_key && <p className="text-xs text-amber-700 flex items-center gap-1.5" data-testid="seed-missing-key"><AlertTriangle className="h-3.5 w-3.5" /> Google Places API key not added yet.</p>}
      {cfg && cfg.places_key && !cfg.abr_guid && <p className="text-xs text-amber-700 flex items-center gap-1.5" data-testid="seed-missing-guid"><AlertTriangle className="h-3.5 w-3.5" /> ABN Lookup GUID not added: ABNs will be left blank.</p>}
      <div className="flex flex-wrap gap-2">
        <Input value={region} onChange={(e) => setRegion(e.target.value)} placeholder="e.g. 2150 or Parramatta NSW" className="flex-1 min-w-[180px]" data-testid="seed-region-input" />
        <Select value={max} onValueChange={setMax}>
          <SelectTrigger className="w-[110px]" data-testid="seed-max-select"><SelectValue /></SelectTrigger>
          <SelectContent>{["20", "40", "60"].map((n) => <SelectItem key={n} value={n}>Up to {n}</SelectItem>)}</SelectContent>
        </Select>
        <Button onClick={start} disabled={running || region.trim().length < 2 || !cfg?.places_key} className="bg-kinetic hover:bg-kinetic-hover text-white gap-2" data-testid="seed-start-btn">
          {running && <Loader2 className="h-4 w-4 animate-spin" />} Search &amp; add
        </Button>
      </div>
      {run && (
        <div className="space-y-2" data-testid="seed-progress">
          <Progress value={run.total ? (run.done / run.total) * 100 : running ? 5 : 100} />
          <p className="text-xs">{running ? "Searching" : run.status === "finished" ? "Finished" : "Stopped"} · {run.region}: {run.found} found · {run.added} added · {run.skipped} skipped · {run.with_abn} with ABN · {run.with_email} with email</p>
          {run.log?.length > 0 && <pre className="text-[11px] bg-muted rounded p-2 max-h-40 overflow-auto whitespace-pre-wrap" data-testid="seed-log">{run.log.join("\n")}</pre>}
        </div>
      )}
    </Card>
  );
}
